(() => {
  'use strict';

  const VERSION = '15.4.7.31';
  let busy = false;

  const normalize = (value) => String(value || '')
    .trim()
    .toUpperCase()
    .replace(/[._-]+/g, ' ')
    .replace(/\b(YOUTH|ADULT|WOMEN|WOMENS|MEN|MENS|UNISEX|APPAREL|SIZE|SIZES)\b/g, ' ')
    .replace(/\s+/g, '')
    .replace(/^EXTRASMALL$/, 'XS')
    .replace(/^SMALL$/, 'S')
    .replace(/^MEDIUM$/, 'M')
    .replace(/^LARGE$/, 'L')
    .replace(/^EXTRALARGE$/, 'XL')
    .replace(/^([2-9])X$/, '$1XL')
    .replace(/^([2-9])XLARGE$/, '$1XL');

  function isMultiMode(designer) {
    const button = designer?.querySelector('[data-me-order-mode="multi"]');
    return Boolean(button && (
      button.classList.contains('is-active') ||
      button.getAttribute('aria-pressed') === 'true'
    ));
  }

  function isReviewStage(designer, button) {
    const reviewPurchase = designer?.querySelector('[data-me-review-purchase]');
    const reviewStep = designer?.querySelector('[data-me-step-button="review"]');
    const label = String(button?.textContent || '').trim().toLowerCase();
    return Boolean(
      (reviewPurchase && !reviewPurchase.hidden) ||
      reviewStep?.classList.contains('is-active') ||
      reviewStep?.getAttribute('aria-current') === 'step' ||
      label.includes('add personalized')
    );
  }

  function projectQuantity(designer) {
    return Array.from(designer?.querySelectorAll?.('[data-me-size-quantity]') || [])
      .reduce((sum, input) => sum + Math.max(0, parseInt(input.value || '0', 10) || 0), 0);
  }

  function isQuoteRequired(designer) {
    if (!designer) return false;
    if (designer.dataset.meCustomApparelQuoteRequired === 'true') return true;
    return projectQuantity(designer) >= 21;
  }

  function isEligible(designer, button) {
    // v15.4.7.40 — 21+ custom apparel is a quote workflow, not a Shopify cart
    // mutation. Let the Studio's dedicated quote handler receive the click instead
    // of uploading artwork / rebuilding size lines and accidentally calling
    // cart/add.js or cart/change.js.
    return Boolean(
      designer &&
      button &&
      isMultiMode(designer) &&
      isReviewStage(designer, button) &&
      !isQuoteRequired(designer)
    );
  }

  function getStatus(designer) {
    return designer.querySelector('[data-me-designer-cart-status]');
  }

  function setStatus(designer, message, isError = false) {
    const status = getStatus(designer);
    if (status) {
      status.textContent = message || '';
      status.classList.toggle('is-error', Boolean(isError));
      status.hidden = false;
    }

    const alert = designer.querySelector('[data-me-review-alert]');
    if (alert) {
      alert.hidden = !message;
      alert.textContent = message || '';
      alert.className = `me-review-alert${message ? (isError ? ' is-error' : ' is-success') : ''}`;
    }
  }

  function setButton(button, disabled, label) {
    if (!button) return;
    button.disabled = disabled;
    button.setAttribute('aria-busy', disabled ? 'true' : 'false');
    const text = button.querySelector('[data-me-primary-action-label], span:first-child');
    if (text && label) text.textContent = label;
  }

  function readProductCandidates(designer) {
    const candidates = [
      window.MomentsSelectedColor?.product,
      window.MomentsProjectState?.product?.selectedColorProduct,
      window.MomentsCurrentProduct
    ];

    const nodes = [
      designer.querySelector('[data-me-initial-product-json]'),
      document.querySelector('[data-me-initial-product-json]')
    ].filter(Boolean);

    nodes.forEach((node) => {
      try {
        const product = JSON.parse(node.textContent || '{}');
        if (product && Array.isArray(product.variants)) candidates.push(product);
      } catch (_) {}
    });

    const seen = new Set();
    return candidates.filter((product) => {
      if (!product || !Array.isArray(product.variants)) return false;
      const key = String(product.id || product.handle || product.title || Math.random());
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  function readProduct(designer) {
    return readProductCandidates(designer)[0] || null;
  }

  function optionIndex(product) {
    const options = Array.isArray(product?.options) ? product.options : [];
    const index = options.findIndex((option) => {
      const name = String(typeof option === 'string' ? option : option?.name || '').toLowerCase();
      return /size/.test(name);
    });
    if (index >= 0) return index;
    return options.length === 1 ? 0 : -1;
  }

  function variantSize(variant, index) {
    if (index >= 0) return variant?.options?.[index] ?? variant?.[`option${index + 1}`] ?? '';
    return variant?.title || '';
  }

  function readDistribution(designer) {
    return Array.from(designer.querySelectorAll('[data-me-size-quantity]'))
      .map((input) => ({
        input,
        size: String(input.dataset.meSizeValue || '').trim(),
        quantity: Math.max(0, parseInt(input.value || '0', 10) || 0),
        storedId: Number(input.dataset.meVariantId || 0)
      }))
      .filter((row) => row.size && row.quantity > 0);
  }


  function findVariantBySize(product, size) {
    if (!product || !Array.isArray(product.variants)) return null;
    const index = optionIndex(product);
    const variants = product.variants.filter((variant) => variant && variant.id);
    let variant = variants.find((item) => normalize(variantSize(item, index)) === normalize(size));
    if (!variant) {
      variant = variants.find((item) => String(item.title || '')
        .split(/\s*\/\s*|,|\|/)
        .some((part) => normalize(part) === normalize(size)));
    }
    if (!variant) {
      variant = variants.find((item) => {
        const values = [item.option1, item.option2, item.option3, ...(Array.isArray(item.options) ? item.options : [])];
        return values.some((value) => normalize(value) === normalize(size));
      });
    }
    return variant || null;
  }

  function resolveVariantAcrossProducts(products, row) {
    // The currently selected color product is authoritative. Variant IDs stored
    // on the size inputs may belong to the product that originally opened the
    // designer, so never allow them to override the current product.
    const currentProduct = products[0];
    const currentVariant = findVariantBySize(currentProduct, row.size);
    if (currentVariant?.id) return currentVariant;

    // Only use another product candidate when the current selected product truly
    // cannot resolve that size. This is a compatibility fallback, not a primary path.
    for (const product of products.slice(1)) {
      const variant = findVariantBySize(product, row.size);
      if (variant?.id) return variant;
    }

    // Some product families (including 1007) render authoritative variant IDs
    // directly on the size quantity controls even when the selected product JSON
    // is temporarily incomplete. Use that ID only after active-product matching
    // has failed, and only when it belongs to one of the loaded product candidates.
    const storedId = Number(row.storedId || row.input?.dataset?.meVariantId || 0);
    if (storedId > 0) {
      for (const product of products) {
        const storedVariant = product?.variants?.find((variant) => Number(variant?.id) === storedId);
        if (storedVariant?.id) return storedVariant;
      }

      // Last compatibility fallback: Shopify variant IDs rendered by Liquid are
      // valid even when the color-product fetch has not completed. This path is
      // reached only when no candidate product can resolve the selected size.
      return { id: storedId, title: row.size, options: [row.size] };
    }

    const visibleRows = Array.from(document.querySelectorAll('[data-me-size-quantity]'));
    const rowIndex = visibleRows.indexOf(row.input);
    if (rowIndex >= 0 && currentProduct?.variants?.[rowIndex]?.id) {
      return currentProduct.variants[rowIndex];
    }
    return null;
  }

  async function loadActiveProduct(designer) {
    const selected = window.MomentsSelectedColor || {};
    const existing = selected.product || window.MomentsProjectState?.product?.selectedColorProduct || window.MomentsCurrentProduct;
    if (existing && Array.isArray(existing.variants) && existing.variants.length) return existing;

    const handle = String(
      selected.handle ||
      designer?.dataset?.meProductHandle ||
      ''
    ).trim();
    const productUrl = String(selected.productUrl || (handle ? `/products/${handle}` : '')).split('?')[0].replace(/\.js$/i, '');
    if (!productUrl) return existing || null;

    const response = await fetch(`${productUrl}.js`, {
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    });
    if (!response.ok) return existing || null;
    const product = await response.json();
    if (product && Array.isArray(product.variants)) {
      window.MomentsCurrentProduct = product;
      window.MomentsSelectedColor = { ...selected, handle: product.handle || handle, product, productUrl };
      window.MomentsColorController?.syncDistributionVariantIds?.(product);
      return product;
    }
    return existing || null;
  }

  function resolveRows(product, rows, designer) {
    const products = readProductCandidates(designer);
    if (product && !products.includes(product)) products.unshift(product);
    if (!products.length) {
      throw new Error('The selected garment data is unavailable. Reselect the color and try again.');
    }

    const missing = [];
    const resolved = rows.map((row) => {
      const variant = resolveVariantAcrossProducts(products, row);
      if (!variant?.id) {
        missing.push(row.size);
        return null;
      }
      row.input.dataset.meVariantId = String(variant.id);
      return { ...row, variantId: Number(variant.id) };
    }).filter(Boolean);

    if (missing.length) {
      throw new Error(`These sizes could not be matched: ${missing.join(', ')}. Return to Product, reselect the garment color, and try again.`);
    }
    return resolved;
  }

  function artworkInputs(designer) {
    const selectedPlacements = new Set(
      Array.from((designer || document).querySelectorAll('[data-me-placement-checkbox]:checked'))
        .map((input) => String(input.value || '').trim())
        .filter(Boolean)
    );
    return Array.from((designer || document).querySelectorAll('[data-me-location-input]'))
      .map((input) => ({
        input,
        placement: String(input.dataset.meLocationInput || '').trim(),
        file: input.files?.[0] || null
      }))
      .filter((entry) => entry.placement && entry.file && selectedPlacements.has(entry.placement));
  }

  function artworkProperties(designer) {
    const properties = {};
    try {
      const form = new FormData();
      window.MEArtworkManager?.appendProperties?.(form);
      for (const [key, value] of form.entries()) {
        const match = String(key).match(/^properties\[(.+)\]$/);
        if (match && typeof value === 'string' && value.trim()) properties[match[1]] = value;
      }

      const assignments = [];
      artworkInputs(designer).forEach(({ placement, file }) => {
        properties[`_Artwork - ${placement}`] = file.name;
        assignments.push(`${placement}: ${file.name}`);
      });

      const managerSummary = window.MEArtworkManager?.getSummary?.() || [];
      managerSummary.forEach((assignment) => {
        const placement = String(assignment?.placement || '').trim();
        const summary = String(assignment?.summary || '').trim();
        const selected = designer?.querySelector?.(`[data-me-placement-checkbox][value="${CSS.escape(placement)}"]`)?.checked;
        if (!placement || !summary || !selected) return;
        if (!properties[`_Artwork - ${placement}`]) properties[`_Artwork - ${placement}`] = summary;
        if (!assignments.some((entry) => entry.startsWith(`${placement}:`))) assignments.push(`${placement}: ${summary}`);
      });

      if (assignments.length) {
        properties['_Production Artwork Assignments'] = assignments.join(' | ');
        properties['_Artwork File Count'] = String(assignments.length);
      }
    } catch (error) {
      console.warn(`[Moments ${VERSION}] Artwork properties were not available.`, error);
    }
    return properties;
  }

  function hostedArtworkProperties(item) {
    const hosted = {};
    Object.entries(item?.properties || {}).forEach(([key, value]) => {
      if (!/^_?(Artwork|Production Preview) - /i.test(key) || typeof value !== 'string') return;
      const raw = value.trim();
      if (!raw) return;
      if (/^https?:\/\//i.test(raw)) hosted[key] = raw;
      else if (/^\/\//.test(raw)) hosted[key] = `${window.location.protocol}${raw}`;
      else if (/^\//.test(raw)) hosted[key] = `${window.location.origin}${raw}`;
    });
    return hosted;
  }

  async function cartError(response, fallback, uploadLabel = '') {
    let text = '';
    try { text = await response.text(); } catch (_) {}
    let data = {};
    try { data = text ? JSON.parse(text) : {}; } catch (_) {}
    const serverMessage = String(data?.description || data?.message || '').trim();
    if (serverMessage) return new Error(serverMessage);

    const status = Number(response?.status || 0);
    const label = uploadLabel ? ` (${uploadLabel})` : '';
    if (status === 413) return new Error(`This artwork file is too large for Shopify to upload${label}. Try a smaller PNG file while keeping the required print resolution.`);
    if (status === 422) return new Error(`${fallback}${label} Shopify rejected the cart request.`);
    return new Error(`${fallback}${label}${status ? ` [Shopify ${status}]` : ''}`);
  }

  function appendItemToForm(form, item, hosted = {}) {
    form.set('id', String(item.id));
    form.set('quantity', String(item.quantity));
    Object.entries({ ...(item.properties || {}), ...hosted }).forEach(([key, value]) => {
      form.set(`properties[${key}]`, String(value ?? ''));
    });
  }

  async function removeLineByKey(root, key) {
    if (!key) return;
    const response = await fetch(`${root}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: 0 })
    });
    if (!response.ok) throw await cartError(response, 'A temporary artwork-upload cart line could not be removed.');
  }

  async function stageHostedUploads(root, first, files, previews, designer) {
    const uploads = [
      ...files.map(({ placement, file }) => ({
        placement, file, property: `_Artwork - ${placement}`, label: `${placement} artwork`
      })),
      ...previews.map(({ placement, file }) => ({
        placement, file, property: `_Production Preview - ${placement}`, label: `${placement} production preview`
      }))
    ];

    if (!uploads.length) return {};

    let hosted = {};
    const reference = String(first?.properties?.['_ME Design Group'] || '').trim();

    for (let index = 0; index < uploads.length; index += 1) {
      const upload = uploads[index];
      const form = new FormData();
      appendItemToForm(form, first, hosted);
      form.set(`properties[${upload.property}]`, upload.file, upload.file.name);

      setStatus(designer, `Preparing project files ${index + 1} of ${uploads.length}…`);
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: form
      });
      if (!response.ok) {
        throw await cartError(response, 'Shopify could not upload the artwork and add the project.', upload.label);
      }

      const stagedLine = await response.json().catch(() => ({}));
      const newlyHosted = hostedArtworkProperties(stagedLine);
      hosted = { ...hosted, ...newlyHosted };

      // Keep a normal string-property mirror of every hosted source artwork URL.
      if (/^_Artwork - /i.test(upload.property) && hosted[upload.property]) {
        const placement = upload.property.replace(/^_Artwork - /i, '').trim();
        if (placement) hosted[`_ME Restore Artwork - ${placement}`] = hosted[upload.property];
      }

      if (!hosted[upload.property]) {
        throw new Error(`Shopify did not return a hosted file link for ${upload.label}. The project was not added so your production artwork cannot be lost.`);
      }

      // v15.4.7.31 — never remove a temporary upload line with the line key
      // returned directly by cart/add.js. Automatic product discounts can cause
      // Shopify to recalculate that key immediately, which makes a subsequent
      // cart/change.js call fail with “no valid id or line parameter”. Instead,
      // re-read cart.js and remove the temporary project by its fresh current
      // keys. The actual project is added only after every file is hosted.
      if (reference) await removePrevious(reference);
    }

    return hosted;
  }


  function waitForPaint() {
    return new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
  }

  function waitForImage(image) {
    if (!image || (image.complete && image.naturalWidth)) return Promise.resolve();
    return new Promise((resolve) => {
      const done = () => resolve();
      image?.addEventListener?.('load', done, { once: true });
      image?.addEventListener?.('error', done, { once: true });
      setTimeout(done, 2500);
    });
  }

  async function imageFromSafeSource(sourceImage) {
    if (!sourceImage?.src) return null;
    try {
      const response = await fetch(sourceImage.currentSrc || sourceImage.src, {
        credentials: 'same-origin',
        cache: 'force-cache'
      });
      if (!response.ok) throw new Error(`Image fetch failed (${response.status})`);
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const image = new Image();
      image.decoding = 'async';
      image.src = objectUrl;
      await waitForImage(image);
      if (!image.naturalWidth) {
        URL.revokeObjectURL(objectUrl);
        return null;
      }
      return { image, objectUrl };
    } catch (error) {
      console.warn(`[Moments ${VERSION}] Preview image could not be localized.`, error);
      return null;
    }
  }

  async function captureCurrentPreview(designer, placement) {
    const stage = designer.querySelector('[data-me-preview-stage]');
    const productImage = designer.querySelector('[data-me-preview-product-image]');
    const artworkImage = designer.querySelector('[data-me-preview-artwork]');
    if (!stage || !productImage?.src) return null;

    await Promise.all([waitForImage(productImage), waitForImage(artworkImage)]);
    await waitForPaint();

    const stageRect = stage.getBoundingClientRect();
    if (!stageRect.width || !stageRect.height) return null;

    const localizedProduct = await imageFromSafeSource(productImage);
    const artworkVisible = artworkImage && !artworkImage.hidden && getComputedStyle(artworkImage).display !== 'none';
    const localizedArtwork = artworkVisible ? await imageFromSafeSource(artworkImage) : null;
    if (!localizedProduct) return null;

    const scale = Math.min(2, Math.max(1, 1200 / Math.max(stageRect.width, stageRect.height)));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(stageRect.width * scale));
    canvas.height = Math.max(1, Math.round(stageRect.height * scale));
    const context = canvas.getContext('2d');
    if (!context) return null;

    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);

    const drawLocalized = (localized, sourceElement) => {
      if (!localized?.image?.naturalWidth || !sourceElement) return;
      const rect = sourceElement.getBoundingClientRect();
      const x = (rect.left - stageRect.left) * scale;
      const y = (rect.top - stageRect.top) * scale;
      const width = rect.width * scale;
      const height = rect.height * scale;
      if (width > 0 && height > 0) context.drawImage(localized.image, x, y, width, height);
    };

    try {
      drawLocalized(localizedProduct, productImage);
      if (localizedArtwork) drawLocalized(localizedArtwork, artworkImage);
    } catch (error) {
      console.warn(`[Moments ${VERSION}] Production preview could not be rendered.`, error);
      return null;
    } finally {
      if (localizedProduct?.objectUrl) URL.revokeObjectURL(localizedProduct.objectUrl);
      if (localizedArtwork?.objectUrl) URL.revokeObjectURL(localizedArtwork.objectUrl);
    }

    let blob = null;
    try {
      blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png', 0.96));
    } catch (error) {
      console.warn(`[Moments ${VERSION}] Production preview could not be exported.`, error);
    }
    if (!blob) return null;
    const safePlacement = String(placement || 'Preview').replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '') || 'Preview';
    return {
      placement: String(placement || 'Preview'),
      file: new File([blob], `ME-${safePlacement}-Production-Preview.png`, { type: 'image/png' })
    };
  }

  async function productionPreviewFiles(designer, placements) {
    const previews = [];
    const cards = Array.from(designer.querySelectorAll('[data-me-navigator-placement]'));
    const activeCard = cards.find((card) => card.classList.contains('is-active') || card.getAttribute('aria-current') === 'true');

    for (const placement of placements) {
      const card = cards.find((candidate) => String(candidate.dataset.meNavigatorPlacement || '').trim() === placement);
      if (card) {
        card.click();
        await waitForPaint();
      }
      const preview = await captureCurrentPreview(designer, placement);
      if (preview) previews.push(preview);
    }

    if (activeCard) {
      activeCard.click();
      await waitForPaint();
    }
    return previews;
  }

  function projectReference(designer) {
    // Existing cart projects explicitly restore their reference onto the designer.
    // Fresh projects must never reuse a session-stored reference from a previous
    // color, product, or completed customization.
    if (designer.dataset.meProjectReference) return designer.dataset.meProjectReference;
    const reference = `me-${crypto.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`}`;
    designer.dataset.meProjectReference = reference;
    return reference;
  }

  function projectDisplayId(designer) {
    // Keep a display ID for the life of this open designer instance only. An edit
    // restore may set the original ID before submission; every fresh page/project
    // receives a new ID and therefore cannot merge with an earlier cart project.
    if (designer.dataset.meProjectDisplayId) return designer.dataset.meProjectDisplayId;
    const now = new Date();
    const date = [now.getFullYear(), String(now.getMonth() + 1).padStart(2, '0'), String(now.getDate()).padStart(2, '0')].join('');
    const suffix = Math.random().toString(36).slice(2, 8).toUpperCase().padEnd(6, '0');
    const displayId = `ME-${date}-${suffix}`;
    designer.dataset.meProjectDisplayId = displayId;
    return displayId;
  }


  async function removePrevious(reference) {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}cart.js`, {
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    });
    if (!response.ok) return;
    const cart = await response.json();
    const updates = {};
    (cart.items || []).forEach((item) => {
      if (item.properties?.['_ME Design Group'] === reference) updates[item.key] = 0;
    });
    if (!Object.keys(updates).length) return;

    const update = await fetch(`${root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates })
    });
    if (!update.ok) {
      const data = await update.json().catch(() => ({}));
      throw new Error(data.description || data.message || 'The previous project could not be updated.');
    }
  }

  async function submit(designer, button) {
    if (busy) return;

    const rights = designer.querySelector('[data-me-rights-checkbox]');
    if (!rights?.checked) throw new Error('Confirm the copyright and artwork authorization before adding this project.');
    if (!window.MomentsCustomAgreement?.validate?.(designer, { focus: true, announce: true })) {
      throw new Error('Complete the required Custom Product Agreement before adding this project.');
    }

    const rows = readDistribution(designer);
    if (!rows.length) throw new Error('Enter at least one size quantity before adding the project.');

    const product = await loadActiveProduct(designer) || readProduct(designer);
    const resolved = resolveRows(product, rows, designer);
    const total = resolved.reduce((sum, row) => sum + row.quantity, 0);
    const color = String(designer.querySelector('[data-me-selected-color]')?.textContent || '')
      .replace(/^Selected:\s*/i, '')
      .trim();
    const placements = Array.from(designer.querySelectorAll('[data-me-placement-checkbox]:checked'))
      .map((input) => input.value);
    const summary = resolved.map((row) => `${row.size}: ${row.quantity}`).join(', ');
    const reference = projectReference(designer);
    const displayId = projectDisplayId(designer);
    const proofRequested = Boolean(designer.querySelector('[data-me-proof-checkbox]')?.checked);
    const neckMode = String(designer.dataset.meNeckLabelMode || designer.querySelector('[data-me-neck-label-choice]:checked')?.value || 'original');
    const neckLabel = neckMode === 'me-branding'
      ? 'Moments Etched Branding — $2 credit'
      : neckMode === 'custom'
        ? 'Your Custom Branding'
        : 'Keep Original Tag';
    const brandingCreditEligible = neckMode === 'me-branding';
    const common = {
      'Project ID': displayId,
      'Order Type': 'Multiple Sizes',
      'Project Total Pieces': String(total),
      'Garment Color': color,
      'Size Distribution': summary,
      'Selected Print Locations': placements.join(', '),
      'Proof Status': proofRequested ? 'Digital proof requested' : 'Standard production review',
      'Inside Neck Label': neckLabel,
      ...(brandingCreditEligible ? {
        '_ME Branding Credit Requested': '$2.00 per garment',
        '_ME Branding Credit Eligible': 'true'
      } : {}),
      '_ME Neck Label Mode': neckMode,
      '_ME Design Group': reference,
      '_ME Line Type': 'garment',
      '_ME Project Version': VERSION,
      ...window.MomentsCustomAgreement.getProperties(designer),
      ...artworkProperties(designer)
    };

    // Build exactly one cart line per selected size. The store-level Shopify
    // add-to-cart limit is configured above the supported bulk-order range, so
    // quantities no longer need to be split into artificial 50-unit batches.
    const garmentItems = resolved.map((row) => ({
      id: row.variantId,
      quantity: row.quantity,
      properties: {
        ...common,
        'Garment Size': row.size,
        '_ME Size Quantity': String(row.quantity)
      }
    }));

    // v14.8.0 financial-integrity hardening: paid print placements must travel
    // with the garment lines. The previous multi-size rebuild path only rebuilt
    // garment variants, which could omit paid placement SKU lines. Shopify still
    // owns the authoritative price for every variant; this code only ensures the
    // required paid add-on variants are included in the cart payload.
    const placementRules = window.MEPlacementRules?.placements || {};
    const paidPlacements = placements.filter((placement) => Number(placementRules[placement]?.priceCents || 0) > 0);
    const missingPaidSku = paidPlacements.find((placement) => !Number(placementRules[placement]?.variantId || 0));
    if (missingPaidSku) throw new Error(`${missingPaidSku} is missing its Shopify paid-placement variant ID.`);

    const addOnItems = paidPlacements.map((placement) => ({
      id: Number(placementRules[placement].variantId),
      quantity: total,
      properties: {
        'Customization': placement,
        '_For Product': String(product?.title || document.title || 'Custom apparel'),
        '_Garment Color': color,
        '_Size Distribution': summary,
        '_ME Design Group': reference,
        '_ME Line Type': 'addon',
        '_ME Project Version': VERSION
      }
    }));

    const items = [...garmentItems, ...addOnItems];

    // Lock the final action before preview capture starts so a fast double-click
    // cannot launch two simultaneous multi-size cart builds.
    busy = true;
    setStatus(designer, 'Preparing artwork for your cart…');
    setButton(button, true, 'Adding Project…');
    try {
      const previews = await productionPreviewFiles(designer, placements);
      await removePrevious(reference);
      const root = window.Shopify?.routes?.root || '/';

      let addedLines = [];
      const files = artworkInputs(designer);

      if ((files.length || previews.length) && items.length) {
        const first = items[0];

        // Shopify file line-item properties are uploaded one binary at a time.
        // v15.4.7.31 uses those cart lines only as temporary upload carriers.
        // After each upload, the temporary project is removed using fresh cart.js
        // keys; then the complete real project is added once with the hosted URLs.
        const hosted = await stageHostedUploads(root, first, files, previews, designer);
        setStatus(designer, 'Adding all size variants and customization pricing…');
        const finalItems = items.map((item) => ({
          ...item,
          properties: { ...item.properties, ...hosted }
        }));
        const finalResponse = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: finalItems })
        });
        if (!finalResponse.ok) throw await cartError(finalResponse, 'Shopify could not add the completed project.');
        const finalAdded = await finalResponse.json().catch(() => ({}));
        addedLines = Array.isArray(finalAdded?.items) ? finalAdded.items : [];
      } else {
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items })
        });
        if (!response.ok) throw await cartError(response, 'Shopify could not add the project to the cart.');
        const added = await response.json().catch(() => ({}));
        addedLines = Array.isArray(added?.items) ? added.items : [];
      }

      const addedGarmentTotal = addedLines
        .filter((line) => String(line?.properties?.['_ME Line Type'] || 'garment') !== 'addon')
        .reduce((sum, line) => sum + (Number(line.quantity) || 0), 0);

      if (addedLines.length && addedGarmentTotal !== total) {
        throw new Error(`Shopify added ${addedGarmentTotal} of ${total} garments. Please try again before checkout.`);
      }

      const cartResponse = await fetch(`${root}cart.js`, {
        headers: { Accept: 'application/json' },
        cache: 'no-store'
      });
      if (!cartResponse.ok) throw new Error('The cart could not be verified after adding the project.');
      const cart = await cartResponse.json();
      const projectLines = (cart.items || []).filter((line) => line.properties?.['_ME Design Group'] === reference);
      const garmentLines = projectLines.filter((line) => line.properties?.['_ME Line Type'] !== 'addon');
      const addonLines = projectLines.filter((line) => line.properties?.['_ME Line Type'] === 'addon');
      const verifiedTotal = garmentLines.reduce((sum, line) => sum + (Number(line.quantity) || 0), 0);

      if (verifiedTotal !== total) {
        throw new Error(`Shopify added ${verifiedTotal} of ${total} garments. Please try again before checkout.`);
      }
      for (const placement of paidPlacements) {
        const paidQty = addonLines
          .filter((line) => String(line.properties?.Customization || '') === placement)
          .reduce((sum, line) => sum + (Number(line.quantity) || 0), 0);
        if (paidQty !== total) {
          throw new Error(`${placement} pricing did not attach to every garment. Please refresh and try again before checkout.`);
        }
      }

      setStatus(designer, `Added ${verifiedTotal} garments with verified customization pricing. Opening cart…`);
      window.location.assign(`${root}cart`);
    } catch (error) {
      // Never leave a half-built project behind when any artwork upload, size line,
      // or paid-placement line fails. The project reference is unique to this build.
      try { await removePrevious(reference); } catch (cleanupError) {
        console.warn(`[Moments ${VERSION}] Partial project cleanup failed.`, cleanupError);
      }
      throw error;
    } finally {
      busy = false;
      setButton(button, false, 'Confirm & Add to Cart');
    }
  }

  async function handleClick(event, button) {
    const designer = button?.closest('[data-me-designer]');
    if (!isEligible(designer, button)) return false;

    event?.preventDefault?.();
    event?.stopPropagation?.();
    event?.stopImmediatePropagation?.();

    try {
      setStatus(designer, 'Preparing your multi-size project…');
      await submit(designer, button);
    } catch (error) {
      console.error(`[Moments ${VERSION}] Add project failed.`, error);
      setStatus(designer, error?.message || 'The project could not be added to the cart.', true);
      setButton(button, false, 'Confirm & Add to Cart');
      busy = false;
    }
    return true;
  }

  // One delegated capture listener is sufficient for both initial and dynamically
  // rendered review buttons. Keeping a single listener prevents accidental double
  // submissions when the designer re-renders during project review.
  document.addEventListener('click', (event) => {
    const button = event.target.closest?.('[data-me-designer-primary-action], [data-me-review-submit]');
    if (button) handleClick(event, button);
  }, true);

})();
